/**
 * src/main/security/RoleEnforcement.ts
 *
 * Server-side & Main-Process Role-Based Access Control (RBAC) Gatekeeper.
 * Validates JWT claims, session scopes, and requested capabilities against
 * the Sovereign Mesh Granular Capability Matrix.
 */

export type UserRole = 'superadmin' | 'mesh_owner' | 'ordinary_user' | 'guest';

export interface RBACContext {
  userId: string;
  role: UserRole;
  permissions?: string[];
  tenantId?: string;
  isStepUpVerified?: boolean;
}

export interface PermissionCheckResult {
  allowed: boolean;
  code: 'RBAC_OK' | 'RBAC_INSUFFICIENT' | 'RBAC_UNAUTHENTICATED' | 'STEP_UP_REQUIRED';
  requiredPermission: string;
  role: UserRole;
  reason?: string;
}

export const CAPABILITY_MATRIX: Record<UserRole, string[]> = {
  guest: [
    'directory.public_read',
  ],
  ordinary_user: [
    'directory.public_read',
    'workspace.read',
    'workspace.write',
    'attendance.clockin',
    'door.access',
    'agent:orchestrator:invoke',
    'agent:knowledge:query',
  ],
  mesh_owner: [
    // Inherits ordinary_user
    'directory.public_read',
    'workspace.read',
    'workspace.write',
    'attendance.clockin',
    'door.access',
    'agent:orchestrator:invoke',
    'agent:knowledge:query',
    // Mesh owner capabilities
    'node.manage',
    'door.acl',
    'attendance.logs',
    'module.install',
    'module.configure',
    'agent:ui:mutate',
    'agent:node:diagnose',
    'credit.allocate',
  ],
  superadmin: [
    // Superadmin has wildcard or all explicit permissions
    '*',
    'directory.public_read',
    'workspace.read',
    'workspace.write',
    'attendance.clockin',
    'door.access',
    'node.manage',
    'door.acl',
    'attendance.logs',
    'module.install',
    'module.configure',
    'module.publish',
    'credit.allocate',
    'platform.admin',
    'cluster.health',
    'global.revoke',
    'door.master_override',
    'agent:orchestrator:invoke',
    'agent:knowledge:query',
    'agent:ui:mutate',
    'agent:node:diagnose',
    'agent:voice:exec',
    'agent:architect:review',
    'agent:emergency:lockdown',
  ],
};

// High-risk capabilities requiring RFC 9470 Step-Up MFA
export const STEP_UP_PROTECTED_PERMISSIONS: Record<string, { minAcr: number; description: string }> = {
  'credit.allocate': { minAcr: 2, description: 'Node credit allocation > 100k' },
  'door.master_override': { minAcr: 2, description: 'Master door override access' },
  'global.revoke': { minAcr: 3, description: 'Emergency session or token revocation' },
  'agent:emergency:lockdown': { minAcr: 3, description: 'Emergency mesh network lockdown' },
  'module.publish': { minAcr: 2, description: 'Publishing global module manifest' },
};

export class RoleEnforcementEngine {
  private static instance: RoleEnforcementEngine;

  private constructor() {}

  public static getInstance(): RoleEnforcementEngine {
    if (!RoleEnforcementEngine.instance) {
      RoleEnforcementEngine.instance = new RoleEnforcementEngine();
    }
    return RoleEnforcementEngine.instance;
  }

  /**
   * Evaluates if an active context holds the required permission.
   */
  public evaluatePermission(
    context: RBACContext | null | undefined,
    requiredPermission: string
  ): PermissionCheckResult {
    if (!context || !context.role) {
      return {
        allowed: false,
        code: 'RBAC_UNAUTHENTICATED',
        requiredPermission,
        role: 'guest',
        reason: 'No active authentication context provided',
      };
    }

    const role = context.role;
    const permissions = CAPABILITY_MATRIX[role] || [];
    const isSuperadmin = permissions.includes('*');

    const hasDirectPermission =
      isSuperadmin ||
      permissions.includes(requiredPermission) ||
      (context.permissions && context.permissions.includes(requiredPermission));

    if (!hasDirectPermission) {
      return {
        allowed: false,
        code: 'RBAC_INSUFFICIENT',
        requiredPermission,
        role,
        reason: `Role '${role}' does not hold required permission '${requiredPermission}'`,
      };
    }

    // Check if the permission requires Step-Up MFA
    const stepUpConfig = STEP_UP_PROTECTED_PERMISSIONS[requiredPermission];
    if (stepUpConfig && !context.isStepUpVerified) {
      return {
        allowed: false,
        code: 'STEP_UP_REQUIRED',
        requiredPermission,
        role,
        reason: `Action '${requiredPermission}' requires Step-Up MFA (ACR level ${stepUpConfig.minAcr}): ${stepUpConfig.description}`,
      };
    }

    return {
      allowed: true,
      code: 'RBAC_OK',
      requiredPermission,
      role,
    };
  }

  /**
   * Express / API Gateway Middleware helper.
   */
  public createMiddleware(requiredPermission: string) {
    return (req: any, res: any, next: any) => {
      const authHeader = req.headers?.authorization || '';
      const token = authHeader.replace(/^Bearer\s+/i, '');
      const user = req.user as RBACContext;

      const result = this.evaluatePermission(user, requiredPermission);
      if (!result.allowed) {
        return res.status(403).json({
          error: 'Forbidden',
          code: result.code,
          requiredPermission,
          reason: result.reason,
        });
      }
      next();
    };
  }
}

export const roleEnforcement = RoleEnforcementEngine.getInstance();
